import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { formatTeacherDate, teacherApi } from "./teacherApi";

export default function TeacherDashboard() {
  const { teacher } = useOutletContext<{ teacher: any }>();
  const [batches, setBatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    teacherApi.myBatches().then(setBatches).finally(() => setLoading(false));
  }, []);

  const todayBatches = batches.filter(
    (b) => String(b.startDate).slice(0, 10) === new Date().toISOString().slice(0, 10)
  );

  return (
    <div className="space-y-6">
      
      {/* 📌 STATS GRID */}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Today's Classes" value={todayBatches.length || "0"} icon="🎥" color="blue" />
        <StatCard title="Assigned Batches" value={batches.length || "0"} icon="👥" color="indigo" />
        <StatCard title="Pending Doubts" value="12" icon="💬" color="orange" />
        <StatCard title="Avg. Rating" value={`${teacher?.rating || "4.8"}/5`} icon="⭐" color="emerald" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.5fr_1fr]">
        
        {/* 📌 LEFT: TODAY'S SCHEDULE (TIMELINE) */}
        <div className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-black text-slate-950">Today's Schedule</h3>
              <p className="text-sm font-bold text-slate-500 mt-1">Aapki aaj ki live classes ki timing.</p>
            </div>
            <Link to="/teach-with-us/schedule" className="text-sm font-black text-blue-600 hover:underline">View Calendar</Link>
          </div>

          <div className="space-y-4">
            {todayBatches.length > 0 ? todayBatches.map((batch, idx) => (
              <div key={idx} className="flex gap-4 p-4 rounded-2xl border border-slate-100 bg-slate-50 hover:bg-blue-50/50 transition">
                <div className="flex flex-col items-center justify-center min-w-[70px] border-r border-slate-200 pr-4">
                  <p className="text-lg font-black text-slate-900">{batch.startTime.slice(0,5)}</p>
                  <p className="text-[10px] font-black uppercase text-slate-400">Start Time</p>
                </div>
                <div className="flex-1">
                  <span className="px-2.5 py-1 bg-blue-100 text-blue-700 text-[10px] font-black uppercase rounded-lg mb-1 inline-block">Live Class</span>
                  <h4 className="text-base font-black text-slate-900">{batch.name}</h4>
                  <p className="text-xs font-bold text-slate-500">{batch.courseTitle}</p>
                </div>
                <div className="flex items-center">
                  <button className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-black rounded-xl shadow-lg shadow-red-600/20 transition">Go Live</button>
                </div>
              </div>
            )) : (
              <div className="py-10 text-center rounded-2xl border border-dashed border-slate-200 bg-slate-50">
                <span className="text-4xl">☕</span>
                <p className="mt-3 text-base font-black text-slate-900">No classes scheduled for today.</p>
                <p className="text-sm font-bold text-slate-500 mt-1">Enjoy your free time or evaluate tests!</p>
              </div>
            )}
          </div>
        </div>

        {/* 📌 RIGHT: QUICK ACTIONS & DOUBTS */}
        <div className="space-y-6">
          <div className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-xl font-black text-slate-950 mb-5">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-3">
              <QuickActionButton icon="📚" label="Upload Notes" to="/teach-with-us/materials" />
              <QuickActionButton icon="📝" label="Create Test" to="/teach-with-us/tests" />
              <QuickActionButton icon="💬" label="Solve Doubts" to="/teach-with-us/doubts" />
              <QuickActionButton icon="👨‍🎓" label="My Students" to="/teach-with-us/students" />
            </div>
          </div>

          <div className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
             <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-black text-slate-950">Recent Batches</h3>
                <Link to="/teach-with-us/batches" className="text-xs font-black text-blue-600 hover:underline">See All</Link>
             </div>
             <div className="space-y-3">
               {loading ? <p className="text-xs font-bold text-slate-400">Loading...</p> : batches.slice(0,3).map(b => (
                 <div key={b.id} className="flex justify-between items-center p-3 rounded-xl border border-slate-100 bg-slate-50">
                    <div>
                      <p className="text-sm font-black text-slate-900">{b.name}</p>
                      <p className="text-[10px] font-bold text-slate-500">Starts: {formatTeacherDate(b.startDate)}</p>
                    </div>
                    <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-[10px] font-black rounded-lg">Active</span>
                 </div>
               ))}
               {!loading && batches.length === 0 && <p className="text-xs font-bold text-slate-400">No batches assigned.</p>}
             </div>
          </div>
        </div>

      </div>
    </div>
  );
}

// Reusable Components
const StatCard = ({ title, value, icon, color }: any) => {
  const colors: Record<string, string> = {
    blue: "bg-blue-50 text-blue-600",
    indigo: "bg-indigo-50 text-indigo-600",
    orange: "bg-orange-50 text-orange-600",
    emerald: "bg-emerald-50 text-emerald-600",
  };
  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm flex items-center gap-4 transition hover:-translate-y-1 hover:shadow-md">
      <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-2xl ${colors[color]}`}>
        {icon}
      </div>
      <div>
        <p className="text-xs font-black uppercase tracking-wider text-slate-400">{title}</p>
        <p className="mt-1 text-2xl font-black text-slate-900">{value}</p>
      </div>
    </div>
  );
};

const QuickActionButton = ({ icon, label, to }: any) => (
  <Link to={to} className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-slate-100 bg-slate-50 p-4 text-center transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700">
    <span className="text-2xl">{icon}</span>
    <span className="text-xs font-black text-slate-700">{label}</span>
  </Link>
);